/**
 * Admin API helpers — typed fetch wrappers ที่ใช้ Better Auth cookies
 */

const BASE =
  (import.meta.env.VITE_API_URL as string | undefined) ?? window.location.origin;

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const init: RequestInit = { method, credentials: 'include' };
  if (body !== undefined) {
    init.headers = { 'content-type': 'application/json' };
    init.body = JSON.stringify(body);
  }
  const res = await fetch(`${BASE}/api/admin${path}`, init);
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`${method} ${path} → ${res.status}: ${text.slice(0, 200)}`);
  }
  return res.json() as Promise<T>;
}

// ============================================================
// Types — keep loose; align with worker schema
// ============================================================
// แบรนด์เป็น "ข้อมูล" (ทะเบียนใน DB) ไม่ใช่ enum ตายตัว — admin สร้างธุรกิจใหม่เองได้
export type Brand = string;
export type CustomerType = 'new' | 'returning';

export interface BrandItem {
  id: number;
  brand_key: string;
  name: string;
  emoji: string | null;
  note: string | null;
  sort_order: number;
  active: number;
}

export interface PricingItem {
  id: number;
  brand: Brand;
  itemKey: string;
  label: string;
  unitType: 'piece' | 'bundle';
  piecesPerUnit: number;
  price: number; // satang
  sortOrder: number;
  active: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PromoItem {
  id: number;
  brand: Brand;
  ruleKey: string;
  name: string;
  description: string | null;
  audience: 'new' | 'returning' | 'all';
  ruleType: 'flat_price_per_unit' | 'tier_percent_off' | 'percent_off' | 'flat_price_coupon';
  config: Record<string, unknown>;
  conditions: Record<string, unknown> | null;
  priority: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ProductImageItem {
  id: number;
  brand: Brand;
  itemKey: string | null;
  imageUrl: string;
  altText: string | null;
  context: 'main' | 'color_variant' | 'size_chart' | 'detail' | 'banner' | 'lifestyle';
  color: string | null;
  sortOrder: number;
  active: boolean;
}

export interface CalcResult {
  brand: Brand;
  customerType: CustomerType;
  requireForm: boolean;
  subtotal: number;
  discount: number;
  total: number;
  appliedPromo: {
    ruleKey: string;
    name: string;
    audience: string;
    ruleType: string;
    computedTotal: number;
    computedDiscount: number;
    reason: string;
  } | null;
  breakdown: Array<{
    itemKey: string;
    label: string;
    qty: number;
    unitPrice: number;
    listPrice: number;
    total: number;
  }>;
  eligiblePromos: Array<{ ruleKey: string; name: string; computedTotal: number }>;
}

// ============================================================
// Order Builder — ช่องข้อมูล + flow รับออเดอร์
// ============================================================
export interface OrderFieldItem {
  id: number;
  brand: string;
  field_key: string;
  label: string;
  source: string; // product | color | size | number | text
  required: number;
  sort_order: number;
  active: number;
}
export interface OrderFlowItem {
  id: number;
  brand: string;
  name: string;
  steps: string; // JSON: [{field,q}]
  summary_template: string | null;
  active: number;
}
export const OrderFieldsApi = {
  list: (brand: Brand) => request<{ fields: OrderFieldItem[] }>('GET', `/order-fields?brand=${brand}`).then((r) => r.fields),
  create: (data: Partial<OrderFieldItem>) => request<{ ok: true; item: OrderFieldItem }>('POST', '/order-fields', data),
  update: (id: number, patch: Partial<OrderFieldItem>) => request<{ ok: true }>('PUT', `/order-fields/${id}`, patch),
  remove: (id: number) => request<{ ok: true }>('DELETE', `/order-fields/${id}`),
};
export const OrderFlowsApi = {
  list: (brand: Brand) => request<{ flows: OrderFlowItem[] }>('GET', `/order-flows?brand=${brand}`).then((r) => r.flows),
  update: (id: number, patch: Partial<OrderFlowItem>) => request<{ ok: true }>('PUT', `/order-flows/${id}`, patch),
};

// ============================================================
// Brands — ทะเบียนแบรนด์/ธุรกิจ
// ============================================================
export const BrandsApi = {
  list: () => request<{ brands: BrandItem[] }>('GET', '/brands').then((r) => r.brands),
  create: (data: Partial<BrandItem>) =>
    request<{ ok: true; item: BrandItem }>('POST', '/brands', data),
  update: (id: number, patch: Partial<BrandItem>) =>
    request<{ ok: true }>('PUT', `/brands/${id}`, patch),
  remove: (id: number) => request<{ ok: true }>('DELETE', `/brands/${id}`),
};

// ============================================================
// Pricing
// ============================================================
export const PricingApi = {
  list: (brand?: Brand) =>
    request<{ pricing: PricingItem[] }>(
      'GET',
      brand ? `/pricing?brand=${brand}` : '/pricing',
    ).then((r) => r.pricing),
  create: (data: Partial<PricingItem>) =>
    request<{ ok: true; item: PricingItem }>('POST', '/pricing', data),
  update: (id: number, patch: Partial<PricingItem>) =>
    request<{ ok: true; item: PricingItem }>('PUT', `/pricing/${id}`, patch),
  remove: (id: number, hard = false) =>
    request<{ ok: true }>('DELETE', `/pricing/${id}${hard ? '?hard=1' : ''}`),
};

// ============================================================
// Promos
// ============================================================
export const PromosApi = {
  list: (brand?: Brand) =>
    request<{ promos: PromoItem[] }>(
      'GET',
      brand ? `/promos?brand=${brand}` : '/promos',
    ).then((r) => r.promos),
  create: (data: Partial<PromoItem>) =>
    request<{ ok: true; item: PromoItem }>('POST', '/promos', data),
  update: (id: number, patch: Partial<PromoItem>) =>
    request<{ ok: true; item: PromoItem }>('PUT', `/promos/${id}`, patch),
  remove: (id: number, hard = false) =>
    request<{ ok: true }>('DELETE', `/promos/${id}${hard ? '?hard=1' : ''}`),
};

// ============================================================
// Product images
// ============================================================
export const ImagesApi = {
  list: (params: { brand?: Brand; itemKey?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.brand) q.set('brand', params.brand);
    if (params.itemKey) q.set('itemKey', params.itemKey);
    const qs = q.toString();
    return request<{ images: ProductImageItem[] }>(
      'GET',
      qs ? `/product-images?${qs}` : '/product-images',
    ).then((r) => r.images);
  },
  create: (data: Partial<ProductImageItem>) =>
    request<{ ok: true; item: ProductImageItem }>('POST', '/product-images', data),
  update: (id: number, patch: Partial<ProductImageItem>) =>
    request<{ ok: true; item: ProductImageItem }>('PUT', `/product-images/${id}`, patch),
  remove: (id: number, hard = false) =>
    request<{ ok: true }>('DELETE', `/product-images/${id}${hard ? '?hard=1' : ''}`),
  /** ลิงก์รูปเพจ (fbid) แสดงตรงไม่ได้ → ผ่าน server แปลงเป็นรูปสด */
  previewSrc: (im: { id: number; imageUrl: string }) =>
    /[?&]fbid=\d+/.test(im.imageUrl) ? `${BASE}/api/admin/product-images/${im.id}/preview` : im.imageUrl,
  /** Pull list of uploaded photos from FB Graph (using stored page token) */
  syncFromFb: (brand: Brand, limit = 50) =>
    request<{
      pageId: string;
      pageName: string;
      brand: Brand;
      count: number;
      photos: Array<{
        fbid: string;
        name: string;
        permalink: string;
        source: string;
        width: number;
        height: number;
        createdTime: string;
      }>;
    }>('GET', `/fb-photos-sync?brand=${brand}&limit=${limit}`),
  /** AI vision classify — รูปนี้เป็นชุดสครับสีอะไร */
  classifyFb: (imageUrl: string) =>
    request<{
      is_scrub: boolean;
      color: string;
      confidence: 'high' | 'medium' | 'low';
      raw?: string;
    }>('POST', '/fb-photo-classify', { imageUrl }),
};

// ============================================================
// Calc + Preview
// ============================================================
export const CalcApi = {
  run: (input: {
    brand: Brand;
    items: Array<{ itemKey: string; qty: number }>;
    customerType: CustomerType;
    requireForm?: boolean;
  }) => request<CalcResult>('POST', '/calc', input),
};

/** สถานะบทสนทนา Preview — server ตรวจกับฐานจริงทุกรอบ หน้าเว็บแค่ส่งกลับไปตามเดิม (ห้ามแก้เอง) */
export type PreviewContext = Record<string, unknown>;
export interface PreviewConversation {
  status: 'collecting' | 'clarifying' | 'out_of_stock' | 'quoted' | 'confirming' | 'payment' | 'draft_order' | 'awaiting_admin' | 'handoff';
  items: { product?: string; color?: string; size?: string; qty?: number; missing: string[] }[];
  captured: string[];
  remembered: string[];
  options: { label: string; value: string }[];
  quote: { subtotal: number; discount: number; total: number; promo: string | null; lines: { label: string; qty: number; total: number }[] } | null;
  draftOrder?: { embroidery: string | null; shipping: string | null } | null;
  orderCreated: false;
  handoffSent: false;
  contextReset: string | null;
}

/** ผลจาก Workers AI (บัญชี Cloudflare เดิม) — ใช้เฉพาะเมื่อระบบกฎตอบไม่ได้ */
export type PreviewAiModel = 'qwen3-30b' | 'gpt-oss-20b';
export interface PreviewAi {
  status: 'answered' | 'rejected' | 'over_budget' | 'error';
  reply: string | null;
  action?: 'faq' | 'ask_order' | 'greet' | 'handoff';
  draft?: string;
  draftFlags?: string[];
  faqQuestions?: string[];
  needsAdmin: boolean;
  reason?: string;
  model: PreviewAiModel;
  usage: { input: number; output: number; neurons: number; todayNeurons: number; cap: number };
  latencyMs: number;
}

export interface PreviewResult {
  pageId: string;
  input: string;
  customerId: null;
  preview: { mode: 'read_only'; customerType: CustomerType; orderFlow: 'skipped' | 'preview_conversation'; semanticSearch: 'disabled' };
  context?: PreviewContext | null;
  conversation?: PreviewConversation;
  ai?: PreviewAi | null;
  reply: string | null;
  reason: string;
  imageUrl?: string | null;
  score?: number;
  sourcePage?: string;
  sourceQ?: string;
}

export interface AssistantDraft {
  pageId: string;
  customerId: number;
  stage: string;
  adminNote: string | null;
  pageName: string;
  brand: string;
  updatedAt: string;
  items: { product: string; color: string; size: string; qty: number }[];
  embroidery: string | null;
  shipping: string | null;
  slipSeen: boolean;
  slip: { status: 'verified' | 'mismatch' | 'manual_review'; reasons: string[]; expected?: number | null; amount?: number | null } | null;
  readyToOrder: boolean;
  version: number;
  total: number | null; // สตางค์ คิดใหม่ ณ ตอนโหลด
}

export const PreviewApi = {
  drafts: () => request<{ drafts: AssistantDraft[] }>('GET', '/assistant/drafts').then((r) => r.drafts),
  release: (d: { pageId: string; brand: string; customerId: number }) => request<{ ok: true; released: number }>('POST', '/assistant/release', d),
  createOrder: (d: { pageId: string; brand: string; customerId: number; seenVersion: number; seenTotal: number }) =>
    request<{ ok: true; orderNo: string; total: number; notified: boolean }>('POST', '/assistant/create-order', d),
  run: (input: { pageId?: string; text: string; previewCustomerType?: CustomerType; context?: PreviewContext | null; ai?: { model: PreviewAiModel; history: { q: string; a: string }[] } }) =>
    request<PreviewResult>('POST', '/preview', {
      text: input.text,
      pageId: input.pageId,
      previewCustomerType: input.previewCustomerType ?? 'new',
      ...(input.context ? { context: input.context } : {}),
      ...(input.ai ? { ai: input.ai } : {}),
    }),
};

export interface SlipTestResult {
  ok: boolean;
  strategy?: 'slipok' | 'ai_vision';
  raw: any;
  check: {
    amountMatch: boolean | null;
    accountMatch: boolean | null;
    expectedAccount: string;
    expectedAccountName: string | null;
    receivedAccount: string;
    receivedAccountName: string | null;
    verifiedAmount: number | null;
    expectedAmount: number | null;
  };
}

export const SlipApi = {
  test: (input: { imageUrl: string; expectedAmount?: number; brand?: Brand }) =>
    request<SlipTestResult>('POST', '/slip-test', input),
};

export interface FaqItem {
  id: number;
  brand: 'scrub' | 'bow' | 'all';
  category: string;
  question: string;
  keywords: string;
  answer: string;
  imageUrl: string | null;
  color: string | null;
  size: string | null;
  priority: number;
  active: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export const FaqApi = {
  list: (brand?: Brand | 'all') =>
    request<{ faqs: FaqItem[] }>(
      'GET',
      brand ? `/faqs?brand=${brand}` : '/faqs',
    ).then((r) => r.faqs),
  create: (data: Partial<FaqItem>) =>
    request<{ ok: true; item: FaqItem }>('POST', '/faqs', data),
  update: (id: number, patch: Partial<FaqItem>) =>
    request<{ ok: true; item: FaqItem }>('PUT', `/faqs/${id}`, patch),
  remove: (id: number, hard = false) =>
    request<{ ok: true }>('DELETE', `/faqs/${id}${hard ? '?hard=1' : ''}`),
  bulkDelete: (ids: number[], hard = false) =>
    request<{ ok: true; count: number }>('POST', '/faqs/bulk-delete', { ids, hard }),
};

// intent_keywords bulk + reorder
export const IntentKeywordsBulkApi = {
  reorder: (items: Array<{ id: number; sortOrder: number }>) =>
    request<{ ok: true; count: number }>('POST', '/intent-keywords/reorder', { items }),
  bulkDelete: (ids: number[], hard = false) =>
    request<{ ok: true; count: number }>('POST', '/intent-keywords/bulk-delete', { ids, hard }),
};

// ============================================================
// Cache invalidate
// ============================================================
export const CacheApi = {
  invalidate: (brand: Brand) =>
    request<{ ok: true }>('POST', '/cache/invalidate', { brand }),
};

// ============================================================
// Brand Settings (1 row per brand)
// ============================================================
export interface BrandSettingsItem {
  id: number;
  brand: Brand;
  displayName: string;
  pageEmoji: string | null;
  productLine: string | null;
  materialDetails: string | null;
  leadTimeText: string | null;
  freeBenefitsText: string | null;
  colorsText: string | null;
  lineUrl: string | null;
  ctaNew: string | null;
  ctaReturning: string | null;
  greetingNew: string | null;
  greetingReturning: string | null;
  bankAccountName: string | null;
  bankAccountNumber: string | null;
  bankName: string | null;
  bankPromptpay: string | null;
  leadProductionDays: string | null;
  leadEmbroideryDays: string | null;
  leadShippingDays: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export const BrandSettingsApi = {
  list: (brand?: Brand) =>
    request<{ settings: BrandSettingsItem[] }>(
      'GET',
      brand ? `/brand-settings?brand=${brand}` : '/brand-settings',
    ).then((r) => r.settings),
  create: (data: Partial<BrandSettingsItem>) =>
    request<{ ok: true; item: BrandSettingsItem }>('POST', '/brand-settings', data),
  update: (id: number, patch: Partial<BrandSettingsItem>) =>
    request<{ ok: true; item: BrandSettingsItem }>('PUT', `/brand-settings/${id}`, patch),
};

// ============================================================
// Compose config — ตั้งค่าการตอบรวมหลายเรื่อง (1 row per brand)
// ============================================================
export interface ComposeConfig {
  id: number;
  brand: string;
  combine_enabled: number;
  max_topics: number;
  topic_order: string;
}
export const ComposeConfigApi = {
  get: (brand: string) =>
    request<{ config: ComposeConfig }>('GET', `/compose-config?brand=${brand}`).then((r) => r.config),
  save: (body: { brand: string; combineEnabled: boolean; maxTopics: number; topicOrder: string }) =>
    request<{ ok: true; config: ComposeConfig }>('PUT', '/compose-config', body),
};

// ============================================================
// นำเข้า Q&A หลายคู่ (วางลิสต์ / อัปรูป)
// ============================================================
export interface ParsedQA {
  question: string;
  answer: string;
  keywords?: string;
  category?: string;
}
export const FaqImportApi = {
  bulkCreate: (brand: string, items: ParsedQA[]) =>
    request<{ ok: true; count: number }>('POST', '/faqs/bulk-create', { brand, items }),
  parseImage: (imageBase64: string, mimeType: string) =>
    request<{ items: ParsedQA[] }>('POST', '/faqs/parse-image', { imageBase64, mimeType }),
};

// ============================================================
// Reply Templates
// ============================================================
export interface ReplyTemplateItem {
  id: number;
  brand: Brand;
  templateKey: string;
  name: string;
  body: string;
  vars: string[] | null;
  imageUrl: string | null;
  active: boolean;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export const ReplyTemplatesApi = {
  list: (brand?: Brand) =>
    request<{ templates: ReplyTemplateItem[] }>(
      'GET',
      brand ? `/reply-templates?brand=${brand}` : '/reply-templates',
    ).then((r) => r.templates),
  create: (data: Partial<ReplyTemplateItem>) =>
    request<{ ok: true; item: ReplyTemplateItem }>('POST', '/reply-templates', data),
  update: (id: number, patch: Partial<ReplyTemplateItem>) =>
    request<{ ok: true; item: ReplyTemplateItem }>(
      'PUT',
      `/reply-templates/${id}`,
      patch,
    ),
  remove: (id: number, hard = false) =>
    request<{ ok: true }>('DELETE', `/reply-templates/${id}${hard ? '?hard=1' : ''}`),
};

// ============================================================
// Intent Keywords
// ============================================================
export type IntentType = 'greeting' | 'promo' | 'image_request' | 'size_chart' | 'thank_you' | 'complaint';

export interface IntentKeywordItem {
  id: number;
  brand: 'scrub' | 'bow' | 'all';
  intentType: IntentType;
  keyword: string;
  sortOrder: number | null;
  notes: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export const IntentKeywordsApi = {
  list: (params: { brand?: string; intentType?: IntentType } = {}) => {
    const q = new URLSearchParams();
    if (params.brand) q.set('brand', params.brand);
    if (params.intentType) q.set('intentType', params.intentType);
    const qs = q.toString();
    return request<{ keywords: IntentKeywordItem[] }>(
      'GET',
      qs ? `/intent-keywords?${qs}` : '/intent-keywords',
    ).then((r) => r.keywords);
  },
  create: (data: Partial<IntentKeywordItem>) =>
    request<{ ok: true; item: IntentKeywordItem }>('POST', '/intent-keywords', data),
  update: (id: number, patch: Partial<IntentKeywordItem>) =>
    request<{ ok: true; item: IntentKeywordItem }>(
      'PUT',
      `/intent-keywords/${id}`,
      patch,
    ),
  remove: (id: number, hard = false) =>
    request<{ ok: true }>('DELETE', `/intent-keywords/${id}${hard ? '?hard=1' : ''}`),
};

// ============================================================
// Brand Colors
// ============================================================
export interface BrandColorItem {
  id: number;
  brand: Brand;
  colorKey: string;
  thaiNames: string;
  displayLabel: string | null;
  aliases: string | null;
  groupLabel: string | null;
  inStock: boolean;
  restockNote: string | null;
  sortOrder: number;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export const BrandColorsApi = {
  list: (brand?: Brand) =>
    request<{ colors: BrandColorItem[] }>(
      'GET',
      brand ? `/brand-colors?brand=${brand}` : '/brand-colors',
    ).then((r) => r.colors),
  create: (data: Partial<BrandColorItem>) =>
    request<{ ok: true; item: BrandColorItem }>('POST', '/brand-colors', data),
  update: (id: number, patch: Partial<BrandColorItem>) =>
    request<{ ok: true; item: BrandColorItem }>('PUT', `/brand-colors/${id}`, patch),
  remove: (id: number, hard = false) =>
    request<{ ok: true }>('DELETE', `/brand-colors/${id}${hard ? '?hard=1' : ''}`),
};

// ============================================================
// Brand Sizes — คลังไซซ์ที่เปิดขาย (admin เพิ่ม/ลบไซซ์ใหม่ได้ เช่น 3XL)
// ============================================================
export interface BrandSizeItem {
  id: number;
  brand: string;
  size_label: string;
  sort_order: number;
  active: number;
}

export const BrandSizesApi = {
  list: (brand: Brand = 'scrub') =>
    request<{ sizes: BrandSizeItem[] }>('GET', `/brand-sizes?brand=${brand}`).then((r) => r.sizes),
  create: (data: { brand: Brand; size_label: string; sort_order?: number }) =>
    request<{ ok: true; item: BrandSizeItem }>('POST', '/brand-sizes', data),
  update: (id: number, patch: Partial<BrandSizeItem>) =>
    request<{ ok: true }>('PUT', `/brand-sizes/${id}`, patch),
  remove: (id: number) => request<{ ok: true }>('DELETE', `/brand-sizes/${id}`),
};

// ============================================================
// Color × Size Stock — สต๊อกราย สี×ไซซ์ (แต่ละสีเลือกไซซ์เองได้)
// ============================================================
export interface ColorSizeStockItem {
  id: number;
  brand: string;
  color_key: string;
  size_label: string;
  in_stock: number;
  note: string | null;
  sort_order: number;
}

export const ColorSizeStockApi = {
  list: (brand: Brand = 'scrub', colorKey?: string) =>
    request<{ stock: ColorSizeStockItem[] }>(
      'GET',
      colorKey
        ? `/color-size-stock?brand=${brand}&color_key=${encodeURIComponent(colorKey)}`
        : `/color-size-stock?brand=${brand}`,
    ).then((r) => r.stock),
  upsert: (data: {
    brand: Brand;
    color_key: string;
    size_label: string;
    in_stock: number;
    note?: string | null;
    sort_order?: number;
  }) => request<{ ok: true }>('PUT', '/color-size-stock', data),
  remove: (brand: Brand, colorKey: string, sizeLabel: string) =>
    request<{ ok: true }>(
      'DELETE',
      `/color-size-stock?brand=${brand}&color_key=${encodeURIComponent(colorKey)}&size_label=${encodeURIComponent(sizeLabel)}`,
    ),
};

// ============================================================
// Size Rules — ตารางแนะนำไซซ์ (admin แก้ได้)
// ============================================================
export interface SizeRuleItem {
  id: number;
  brand: string;
  garment: 'shirt' | 'pants';
  measure: 'chest' | 'waist';
  min_val: number;
  max_val: number;
  size: string;
  active: number;
  notes: string | null;
}

export const SizeRulesApi = {
  list: (brand = 'scrub') =>
    request<{ rules: SizeRuleItem[] }>('GET', `/size-rules?brand=${brand}`).then((r) => r.rules),
  create: (data: Partial<SizeRuleItem>) =>
    request<{ ok: true; item: SizeRuleItem }>('POST', '/size-rules', data),
  update: (id: number, patch: Partial<SizeRuleItem>) =>
    request<{ ok: true }>('PUT', `/size-rules/${id}`, patch),
  remove: (id: number) => request<{ ok: true }>('DELETE', `/size-rules/${id}`),
};

// ============================================================
// KB Browser — knowledge_base (Q&A จากแชทจริง ที่บอทใช้ตอบ)
// ============================================================
export interface KbItem {
  id: number;
  brand: string;
  question: string;
  answer: string;
  embedding_id: string | null;
  active: number;
  source: string | null;
  usage_count: number;
}

export type KbFilter = 'all' | 'junk';
export type KbSort = 'default' | 'used';

function kbQs(brand: string, q: string, page: number, filter: KbFilter, sort: KbSort) {
  return `brand=${brand}&q=${encodeURIComponent(q)}&page=${page}&filter=${filter}&sort=${sort}`;
}

export const KbApi = {
  list: (brand = 'scrub', q = '', page = 1, filter: KbFilter = 'all', sort: KbSort = 'default') =>
    request<{ items: KbItem[]; total: number; page: number; pageSize: number }>(
      'GET',
      `/kb?${kbQs(brand, q, page, filter, sort)}`,
    ),
  update: (id: number, patch: { answer?: string; question?: string; active?: number | boolean }) =>
    request<{ ok: true }>('PUT', `/kb/${id}`, patch),
  remove: (id: number) => request<{ ok: true }>('DELETE', `/kb/${id}`),
  bulk: (action: 'disable' | 'enable', brand = 'scrub', q = '', filter: KbFilter = 'junk') =>
    request<{ ok: true; count: number }>(
      'POST',
      `/kb/bulk?${kbQs(brand, q, 1, filter, 'default')}`,
      { action },
    ),
  suggestFaq: (brand = 'scrub') =>
    request<{ suggestions: KbFaqSuggestion[] }>('GET', `/kb/suggest-faq?brand=${brand}`).then(
      (r) => r.suggestions,
    ),
  mineFaq: (brand = 'scrub') =>
    request<{ candidates: KbMinedQuestion[]; total: number }>('GET', `/kb/mine-faq?brand=${brand}`),
};

export interface KbMinedQuestion {
  question: string;
  answer: string;
  count: number;
}

export interface KbFaqSuggestion {
  key: string;
  question: string;
  category: string;
  keywords: string;
  answer: string;
  count: number;
  cleanCount: number;
  alreadyInFaq: boolean;
}

// ============================================================
// KB Intents + Analysis Rules — สมองวิเคราะห์ (admin แก้ได้ NO HARDCODE)
// ============================================================
export interface KbIntentItem {
  id: number;
  brand: string;
  intent_key: string;
  question: string;
  category: string;
  keywords: string;
  answer_keywords: string;
  active: number;
  sort_order: number;
}
export const KbIntentsApi = {
  list: (brand = 'scrub') =>
    request<{ items: KbIntentItem[] }>('GET', `/kb-intents?brand=${brand}`).then((r) => r.items),
  create: (data: Partial<KbIntentItem>) => request<{ ok: true }>('POST', '/kb-intents', data),
  update: (id: number, patch: Partial<KbIntentItem>) => request<{ ok: true }>('PUT', `/kb-intents/${id}`, patch),
  remove: (id: number) => request<{ ok: true }>('DELETE', `/kb-intents/${id}`),
};

export interface AnalysisRuleItem {
  id: number;
  brand: string;
  // junk / multicolor / color_kw / stock_kw / measure_kw / order_verb / order_unit (ขยายได้อิสระ)
  rule_type: string;
  pattern: string;
  note: string | null;
  active: number;
}
export const AnalysisRulesApi = {
  list: (brand = 'scrub') =>
    request<{ items: AnalysisRuleItem[] }>('GET', `/analysis-rules?brand=${brand}`).then((r) => r.items),
  create: (data: Partial<AnalysisRuleItem>) => request<{ ok: true }>('POST', '/analysis-rules', data),
  update: (id: number, patch: Partial<AnalysisRuleItem>) => request<{ ok: true }>('PUT', `/analysis-rules/${id}`, patch),
  remove: (id: number) => request<{ ok: true }>('DELETE', `/analysis-rules/${id}`),
};

// ============================================================
// Page Settings
// ============================================================
export interface PageSettingsItem {
  id: number;
  pageId: string;
  pageName: string;
  brand: Brand;
  replyEnabled: boolean;
  isTestPage: boolean;
  testPsids: string | null;
  adminPsids: string | null;
  fallbackText: string | null;
  greetingText: string | null;
  /** Always masked '***' from API; never raw */
  accessTokenEncrypted: string | null;
  verifyTokenEncrypted: string | null;
  hasAccessToken: boolean;
  hasVerifyToken: boolean;
  fbAppId: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Write-only fields — pass plain text; server encrypts before save */
export interface PageSettingsWrite extends Partial<PageSettingsItem> {
  accessToken?: string;
  verifyToken?: string;
}

export const PageSettingsApi = {
  list: () =>
    request<{ pages: PageSettingsItem[] }>('GET', '/page-settings').then((r) => r.pages),
  create: (data: PageSettingsWrite) =>
    request<{ ok: true; item: PageSettingsItem }>('POST', '/page-settings', data),
  update: (id: number, patch: PageSettingsWrite) =>
    request<{ ok: true; item: PageSettingsItem }>('PUT', `/page-settings/${id}`, patch),
  remove: (id: number) =>
    request<{ ok: true }>('DELETE', `/page-settings/${id}`),
  subscribe: (id: number) =>
    request<{ ok: true; response: unknown }>('POST', `/page-settings/${id}/subscribe`),
};

// ============================================================
// FB OAuth — เชื่อมต่อ Facebook → save tokens encrypted ลง page_settings ครั้งเดียวจบ
// ============================================================
export const FbOAuthApi = {
  /** Returns FB consent URL; user clicks to authorize */
  start: () => request<{ authorizeUrl: string }>('POST', '/fb-oauth/start'),
};

// ============================================================
// Admin Users (owner-only writes)
// ============================================================
export type AdminRole = 'owner' | 'admin' | 'operator' | 'viewer';

export interface AdminUserItem {
  id: number;
  email: string;
  name: string | null;
  role: AdminRole;
  authUserId: string | null;
  active: boolean;
  lastLoginAt: string | null;
  createdByEmail: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export const AdminsApi = {
  list: () =>
    request<{ admins: AdminUserItem[] }>('GET', '/admins').then((r) => r.admins),
  create: (data: Partial<AdminUserItem>) =>
    request<{ ok: true; item: AdminUserItem }>('POST', '/admins', data),
  update: (id: number, patch: Partial<AdminUserItem>) =>
    request<{ ok: true; item: AdminUserItem }>('PUT', `/admins/${id}`, patch),
  remove: (id: number, hard = false) =>
    request<{ ok: true }>('DELETE', `/admins/${id}${hard ? '?hard=1' : ''}`),
};

// ============================================================
// Analytics + Chat Refresh
// ============================================================
export interface AnalyticsResult {
  sinceISO: string;
  days: number;
  totals: {
    messages: number;
    active_customers: number;
    total_customers: number;
    orders_in_period: number;
  } | null;
  volumePerDay: Array<{ day: string; direction: 'in' | 'out'; count: number }>;
  topIntents: Array<{ ai_intent: string; count: number }>;
  customerTypes: Array<{ type: 'new' | 'returning'; count: number }>;
}

export const AnalyticsApi = {
  get: (days = 7) =>
    request<AnalyticsResult>('GET', `/analytics?days=${days}`),
  performance: (days = 7) =>
    request<PerformanceResult>('GET', `/analytics/performance?days=${days}`),
};

export interface PerformanceResult {
  sinceISO: string;
  days: number;
  repliesBySender: Array<{ sender_type: 'customer' | 'bot' | 'admin'; count: number }>;
  responseTime: Array<{
    out_sender: string;
    replies: number;
    avg_seconds: number | null;
    min_seconds: number | null;
    max_seconds: number | null;
  }>;
  coverage: {
    total_in: number;
    replied: number;
    unanswered: number;
  } | null;
  kbHitRate: {
    total: number;
    with_intent: number;
    avg_confidence: number | null;
  } | null;
  topAdmin: Array<{ sender: string; count: number }>;
}

export const ChatApi = {
  refresh: (pageId?: string) =>
    request<{ ok: true; status: string; note?: string }>('POST', '/chat/refresh', { pageId }),
};

// ============================================================
// Audit log
// ============================================================
export interface AuditEntry {
  id: number;
  actorEmail: string;
  action: string;
  resourceType: string | null;
  resourceId: number | null;
  beforeValue: Record<string, unknown> | null;
  afterValue: Record<string, unknown> | null;
  ipAddress: string | null;
  createdAt: string;
}

export const AuditApi = {
  list: (params: { limit?: number; action?: string; resourceType?: string } = {}) => {
    const q = new URLSearchParams();
    if (params.limit) q.set('limit', String(params.limit));
    if (params.action) q.set('action', params.action);
    if (params.resourceType) q.set('resourceType', params.resourceType);
    const qs = q.toString();
    return request<{ entries: AuditEntry[] }>('GET', qs ? `/audit?${qs}` : '/audit').then(
      (r) => r.entries,
    );
  },
};

// ============================================================
// Image upload (multipart) → R2 → URL
// ============================================================
export const UploadApi = {
  uploadImage: async (
    file: File,
    brand: Brand,
    itemKey?: string,
    color?: string,
  ): Promise<{ ok: true; url: string; key: string }> => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('brand', brand);
    if (itemKey) fd.append('itemKey', itemKey);
    if (color) fd.append('color', color);
    const res = await fetch(`${BASE}/api/admin/upload-image`, {
      method: 'POST',
      credentials: 'include',
      body: fd,
    });
    if (!res.ok) {
      const t = await res.text().catch(() => '');
      throw new Error(`upload failed ${res.status}: ${t.slice(0, 200)}`);
    }
    return res.json();
  },
};

// ============================================================
// Helpers
// ============================================================
export function satangToBaht(satang: number): string {
  return (satang / 100).toLocaleString('th-TH', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

export function bahtToSatang(baht: number | string): number {
  return Math.round(Number(baht) * 100);
}

// ============================================================
// Ads insights (Meta) — ผลโฆษณา spend/ROAS/ผลลัพธ์ แยกแคมเปญ
// ============================================================
export interface AdsCampaign {
  name: string; spend: number; revenue: number; roas: number;
  purchases: number; orders: number; leads: number; ctr: number; cpc: number; frequency: number;
}
export interface AdsInsights {
  account: string; range: string; fetchedAt: string; cached?: boolean;
  overview: { spend: number; revenue: number; roas: number; reach: number; impressions: number; frequency: number; ctr: number; cpc: number; purchases: number; orders: number; leads: number };
  campaigns: AdsCampaign[];
  error?: string; hint?: string; detail?: string;
}
export const AdsApi = {
  insights: (range = 'last_30d', refresh = false) =>
    request<AdsInsights>('GET', `/ads-insights?range=${range}${refresh ? '&refresh=1' : ''}`),
};
